import React, { useEffect, useMemo, useRef, useState } from 'react';
import FilterExposedHeader from '../FilterExposedHeader';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import useAnimations from '../../../hooks/Animations/useAnimations';
import { FilterFrame, FilterSelection, GroupWrapper } from '../Filters.style';
import CheckboxSelector from '../../CheckboxComponent';
import {
  AllMakesWrapper,
  GroupMakeLabel,
  MakeCheckbox,
  MakesCount,
  OtherMakeGroupWrapper,
  ShowAllButton,
} from '../OtherMakes/OtherMakes.style';

interface CarData {
  active: boolean;
  code: string;
  count: number;
  value: string;
  label: string;
  ariaLabel: string;
  makeLabel: string;
  makeCode: string;
}
interface GroupedByMake {
  [make: string]: {
    makeCode: string;
    listOfModels: string[];
  };
}
interface NonAudiModelsProps {
  filterType: string;
  nonAudiModelsOptions: any;
  confirmedFilters: FilterTypes;
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string) => void;
  handleExposeFilter: (arg: string) => void;
}

const NonAudiModels: React.FC<NonAudiModelsProps> = ({
  filterType,
  nonAudiModelsOptions,
  confirmedFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
}) => {
  const listOfOptions = useMemo(() => {
    if (!nonAudiModelsOptions || !Object.values(nonAudiModelsOptions)?.length) return [];
    const groupByMake = (Object.values(nonAudiModelsOptions) as CarData[])?.reduce((acc, car) => {
      if (!acc[car.makeLabel]) {
        acc[car.makeLabel] = {
          makeCode: car.makeCode,
          listOfModels: [],
        };
      }
      acc[car.makeLabel].listOfModels.push(car.code);
      return acc;
    }, {} as GroupedByMake);
    return Object.entries(groupByMake)
      .map(([makeLabel, models]) => ({
        makeLabel,
        makeCode: models.makeCode,
        models: models.listOfModels,
      }))
      .filter((option) => confirmedFilters.otherMakes.includes(option.makeCode));
  }, [nonAudiModelsOptions, confirmedFilters.otherMakes]);

  const selectedNonAudiLabels = useMemo(() => {
    const selectedNonAudiList = confirmedFilters.nonAudiModels;
    return selectedNonAudiList.map(
      (model) => `${nonAudiModelsOptions[model]?.makeLabel} ${nonAudiModelsOptions[model]?.label}`,
    );
  }, [nonAudiModelsOptions, confirmedFilters.nonAudiModels]);

  const showFilter = openedFiltersList[filterType];

  const selectedNonAudiFilters = useMemo(() => {
    return confirmedFilters?.nonAudiModels;
  }, [confirmedFilters?.nonAudiModels]);

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  return (
    <>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedNonAudiLabels}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="other-models-filter-group">
              <FilterFrame style={filtersAccordianAnimation}>
                <AllMakesWrapper>
                  {listOfOptions.map((makeGroup, index) => {
                    const { makeLabel, models } = makeGroup;
                    return (
                      <div key={makeLabel}>
                        <GroupMakeLabel>{makeLabel} models</GroupMakeLabel>
                        <OtherMakeGroupWrapper>
                          {models.map((model) => {
                            const { label, value, count } = nonAudiModelsOptions[model];
                            return (
                              <MakeCheckbox key={label}>
                                <CheckboxSelector
                                  text={label}
                                  key={value}
                                  value={value}
                                  filterType={filterType}
                                  onChange={(newValue) => handleAddFilter(filterType, newValue)}
                                  inputName={`${value}-${index}`}
                                  checked={selectedNonAudiFilters.includes(value)}
                                />
                                <MakesCount>({count})</MakesCount>
                              </MakeCheckbox>
                            );
                          })}
                        </OtherMakeGroupWrapper>
                      </div>
                    );
                  })}
                </AllMakesWrapper>
              </FilterFrame>
            </FilterSelection>
          ),
      )}
    </>
  );
};

export default NonAudiModels;
